import React, { useState } from "react";
import { Alert, Button, Card, Col, Input, InputNumber, Radio, Row, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { saveSettings } from "./api";
import { normalizeSettings, wageRate } from "./calc";
import {
  bpToPercent,
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatEuro,
  percentToBp
} from "./format";

/** Kalkulation: wage groups, Materialzuschlag (by Warengruppe), Gemeinkosten, Wagnis & Gewinn. */
const SettingsTab = ({ meta, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const numbers = decimalInputProps(lng);
  const [form, setForm] = useState(() => normalizeSettings(meta.settings));
  const [busy, setBusy] = useState(false);
  const canEdit = meta.permissions.update;
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setRow = (list, index, key, value) =>
    set(
      list,
      form[list].map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );

  const save = async () => {
    setBusy(true);
    try {
      const res = await saveSettings(form);
      Success(t("Calculation settings saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const percent = (value, onChange, min = 0) => (
    <InputNumber
      {...numbers}
      className="mat-full"
      min={min}
      max={1000}
      addonAfter="%"
      value={bpToPercent(value)}
      onChange={v => onChange(percentToBp(v))}
      disabled={!canEdit}
    />
  );
  const euro = (value, onChange) => (
    <InputNumber
      {...numbers}
      className="mat-full"
      min={0}
      precision={2}
      addonAfter="€/h"
      value={value ? centsToEuro(value) : null}
      onChange={v => onChange(euroToCents(v))}
      disabled={!canEdit}
    />
  );

  return (
    <div>
      <Alert
        className="mat-explain"
        type="info"
        showIcon
        message={t("How prices are calculated")}
        description={
          <Typography.Paragraph className="mat-wrap">
            {t(
              "Material: purchase price after the supplier discount × (1 + material markup of the product group). Labour: minutes × hourly rate of the wage group; without an entered rate it is wage cost × (1 + overhead) × (1 + risk and profit). The unit price is material plus labour, rounded to the cent. The contribution margin is the price minus material cost and wage cost."
            )}
          </Typography.Paragraph>
        }
      />

      <Card title={t("Wage groups")} className="mat-section">
        {form.wageGroups.map((g, index) => (
          <div className="mat-row-editor" key={index}>
            <Input
              placeholder={t("Code")}
              value={g.code}
              onChange={e => setRow("wageGroups", index, "code", e.target.value)}
              disabled={!canEdit}
            />
            <Input
              placeholder={t("Name")}
              value={g.name}
              onChange={e => setRow("wageGroups", index, "name", e.target.value)}
              disabled={!canEdit}
            />
            <div>
              <div className="mat-muted">{t("Wage cost")}</div>
              {euro(g.costRate, v => setRow("wageGroups", index, "costRate", v))}
            </div>
            <div>
              <div className="mat-muted">
                {t("Hourly rate")} ({t("used")}: {formatEuro(wageRate(g, form), lng)})
              </div>
              {euro(g.rate, v => setRow("wageGroups", index, "rate", v))}
            </div>
            <Space>
              <Radio
                checked={form.defaultWageGroup === g.code}
                onChange={() => set("defaultWageGroup", g.code)}
                disabled={!canEdit}
              >
                {t("Default")}
              </Radio>
              {canEdit && form.wageGroups.length > 1 ? (
                <Button
                  type="link"
                  danger
                  onClick={() =>
                    set(
                      "wageGroups",
                      form.wageGroups.filter((_, i) => i !== index)
                    )
                  }
                >
                  {t("Remove")}
                </Button>
              ) : null}
            </Space>
          </div>
        ))}
        {canEdit ? (
          <Button
            type="dashed"
            block
            className="mat-section"
            onClick={() =>
              set("wageGroups", [...form.wageGroups, { code: "", name: "", rate: 0, costRate: 0 }])
            }
          >
            + {t("Add wage group")}
          </Button>
        ) : null}
      </Card>

      <Card title={t("Markups")} className="mat-section">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <div className="mat-muted">{t("Material markup")}</div>
            {percent(form.materialMarkup, v => set("materialMarkup", v), -100)}
          </Col>
          <Col xs={24} md={8}>
            <div className="mat-muted">{t("Overhead on wages")}</div>
            {percent(form.overhead, v => set("overhead", v))}
          </Col>
          <Col xs={24} md={8}>
            <div className="mat-muted">{t("Risk and profit")}</div>
            {percent(form.riskProfit, v => set("riskProfit", v))}
          </Col>
        </Row>
        <Typography.Title level={5} className="mat-section">
          {t("Material markup by product group")}
        </Typography.Title>
        {form.categoryMarkups.map((c, index) => (
          <Space key={index} wrap className="mat-full mat-section">
            <Input
              placeholder={t("Product group (prefix)")}
              value={c.category}
              onChange={e => setRow("categoryMarkups", index, "category", e.target.value)}
              disabled={!canEdit}
            />
            {percent(c.markup, v => setRow("categoryMarkups", index, "markup", v), -100)}
            {canEdit ? (
              <Button
                type="link"
                danger
                onClick={() =>
                  set(
                    "categoryMarkups",
                    form.categoryMarkups.filter((_, i) => i !== index)
                  )
                }
              >
                {t("Remove")}
              </Button>
            ) : null}
          </Space>
        ))}
        {canEdit ? (
          <Button
            type="dashed"
            block
            className="mat-section"
            onClick={() =>
              set("categoryMarkups", [
                ...form.categoryMarkups,
                { category: "", markup: form.materialMarkup }
              ])
            }
          >
            + {t("Add product group")}
          </Button>
        ) : null}
      </Card>

      {canEdit ? (
        <Button type="primary" className="mat-section" loading={busy} onClick={save}>
          {t("Save")}
        </Button>
      ) : null}
    </div>
  );
};

export default SettingsTab;
